import { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import "./ProjectCode.css";
import axiosInstance from "../../../../Config/axiosInstance";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import EditNoteSharpIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ProjectCodeCreateDialog from "./ProjectCodeCreateDialog";
import ProjectCodeEditDialog from "./ProjectCodeEditDialog";
import ProjectCodeDeleteDialog from "./ProjectCodeDeleteDialog";
import SubProjectCodeCreate from "./SubProjectCodeCreate";

function ProjectCode() {
  const [canCreate, setCanCreate] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [canViewSubproject, setCanViewSubproject] = useState(false);
  const [canCreateSubproject, setCanCreateSubproject] = useState(false);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const [openCreateSub, setOpenCreateSub] = useState(false);
  const [selectedProjectForSub, setSelectedProjectForSub] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const requiredPath = "/project_codes";
    const subprojectPath = "/sub_project_codes";

    const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
    const permissions = storedPermissions?.pages || [];

    const safePath = (url) => {
      try {
        return url?.startsWith("/") ? url : new URL(url).pathname;
      } catch {
        return url || "";
      }
    };

    setCanCreate(
      permissions.some(
        (page) =>
          safePath(page.url) === requiredPath && page.can_create === true
      )
    );

    setCanEdit(
      permissions.some(
        (page) =>
          safePath(page.url) === requiredPath && page.can_edit === true
      )
    );

    setCanDelete(
      permissions.some(
        (page) =>
          safePath(page.url) === requiredPath && page.can_delete === true
      )
    );

    setCanViewSubproject(
      permissions.some(
        (page) =>
          safePath(page.url) === subprojectPath && page.can_view === true
      )
    );

    setCanCreateSubproject(
      permissions.some(
        (page) =>
          safePath(page.url) === subprojectPath && page.can_create === true
      )
    );
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const codeRegistriesResponse = await axiosInstance.get("/code_registries/");

      const sortedData = (codeRegistriesResponse.data || [])
        .map((item) => {
          return {
            id:
              item.id ??
              item.code ??
              item.nr ??
              `${item.code}-${item.created_at}`,

            nr: item.nr ?? "",
            description: item.description ?? "",
            code: item.code ?? "",
            status: item.status ?? "",
            created_at: item.created_at ?? null,
            updated_at: item.updated_at ?? null,
            remarks: item.remarks ?? "",
            matrix: item.matrix ?? null,
            activity: item.activity_label ?? "-",
            department: item.department_label ?? "-",
            client: item.client_label ?? "-",

            hasSubprojects: item.has_subprojects === true,

            __raw: item,
          };
        })
        .sort(
          (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
        );

      setRows(sortedData);
    } catch (error) {
      console.error(
        "Error fetching data:",
        error.response?.data || error.message
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [location.pathname, refreshKey, fetchData]);

  const triggerRefresh = () => setRefreshKey((k) => k + 1);

  const openEditDialog = useCallback(
    (row) => {
      if (!canEdit) return;
      setSelectedRow(row);
      setOpenEdit(true);
    },
    [canEdit]
  );

  const openDeleteDialog = useCallback(
    (row) => {
      if (!canDelete) return;
      setSelectedRow(row);
      setOpenDelete(true);
    },
    [canDelete]
  );

  const openCreateSubDialog = useCallback(
    (row) => {
      if (!canCreateSubproject) return;
      setSelectedProjectForSub(row);
      setOpenCreateSub(true);
    },
    [canCreateSubproject]
  );

  const goToSubProjectPage = useCallback(
    (row) => {
      if (!canViewSubproject) return;

      const projectId = row.__raw?.id ?? row.id;

      navigate(`/sub_project_codes/project=${projectId}`, {
        state: {
          projectCodeId: projectId,
          projectCode: row.code,
        },
      });
    },
    [navigate, canViewSubproject]
  );

  const columns = useMemo(() => {
    const base = [
      {
        field: "nr",
        headerName: "Nr",
        flex: 0.3,
        minWidth: 60,
        editable: false,
        display: "flex",
        renderCell: ({ value }) => (
          <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {value}
          </div>
        ),
      },
      {
        field: "department",
        headerName: "Deparment",
        flex: 0.8,
        minWidth: 120,
        editable: false,
        display: "flex",
        renderCell: ({ value }) => (
          <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {value ?? "-"}
          </div>
        ),
      },
      {
        field: "activity",
        headerName: "Activity",
        flex: 0.8,
        minWidth: 170,
        editable: false,
        display: "flex",
        renderCell: ({ value }) => (
          <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {value ?? "-"}
          </div>
        ),
      },
      {
        field: "client",
        headerName: "Client",
        flex: 1,
        minWidth: 200,
        editable: false,
        display: "flex",
        renderCell: ({ value }) => (
          <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {value ?? "-"}
          </div>
        ),
      },
      {
        field: "code",
        headerName: "Code",
        flex: 1.2,
        minWidth: 240,
        editable: false,
        display: "flex",
        renderCell: ({ value }) => (
          <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {value}
          </div>
        ),
      },
      {
        field: "description",
        headerName: "Description",
        flex: 1.2,
        minWidth: 220,
        editable: false,
        display: "flex",
        renderCell: ({ value }) => (
          <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {value}
          </div>
        ),
      },
      {
        field: "status",
        headerName: "Project Status",
        width: 150,
        editable: false,
        display: "flex",
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const status = params.value?.toLowerCase() || "";

          return (
            <div className={`status-badge ${status}`}>
              {status === "completed" ? (
                <CheckCircleIcon className="status-icon" />
              ) : (
                <RadioButtonUncheckedIcon className="status-icon" />
              )}
              <span>{status === "completed" ? "Completed" : "Active"}</span>
            </div>
          );
        },
      },
    ];

    if (canViewSubproject || canCreateSubproject || canEdit || canDelete) {
      base.push({
        field: "actions",
        headerName: "Actions",
        width: 220,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <>
            {canViewSubproject && (
              <Tooltip
                title={
                  params.row.hasSubprojects
                    ? "View existing Sub Project Codes"
                    : "No Sub Project Codes available"
                }
                arrow
              >
                <IconButton
                  sx={{
                    color: params.row.hasSubprojects ? "#6a1b9a" : "#757575",
                  }}
                  onClick={() => goToSubProjectPage(params.row)}
                >
                  <VisibilityIcon />
                </IconButton>
              </Tooltip>
            )}

            {canCreateSubproject && (
              <Tooltip title="Add Sub Project Code" arrow>
                <IconButton
                  sx={{ color: "#2e7d32" }}
                  onClick={() => openCreateSubDialog(params.row)}
                >
                  <AddCircleOutlineIcon />
                </IconButton>
              </Tooltip>
            )}

            {canEdit && (
              <Tooltip title="Edit" arrow>
                <IconButton
                  sx={{ color: "#1976d2" }}
                  onClick={() => openEditDialog(params.row)}
                >
                  <EditNoteSharpIcon />
                </IconButton>
              </Tooltip>
            )}

            {canDelete && (
              <Tooltip title="Delete" arrow>
                <IconButton
                  sx={{ color: "#d32f2f" }}
                  onClick={() => openDeleteDialog(params.row)}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </>
        ),
      });
    }

    return base;
  }, [
    canViewSubproject,
    canCreateSubproject,
    canEdit,
    canDelete,
    openCreateSubDialog,
    openEditDialog,
    openDeleteDialog,
    goToSubProjectPage,
  ]);

  return (
    <div className="code-matrix-page">
      {canCreate && (
        <div className="add-button-matrix">
          <Button
            variant="contained"
            disabled={!canCreate}
            sx={{ textTransform: "none" }}
            style={{ float: "right", marginBottom: "15px" }}
            onClick={() => setOpenCreate(true)}
          >
            <AddIcon />
            ADD PROJECT CODE
          </Button>
        </div>
      )}

      <div className="table-container">
        <DataGrid
          id="project-code-table"
          loading={loading}
          rows={rows}
          columns={columns}
          getRowHeight={() => "auto"}
          initialState={{
            sorting: {
              sortModel: [{ field: "created_at", sort: "desc" }],
            },
            pagination: {
              paginationModel: { pageSize: 10 },
            },
          }}
          pageSizeOptions={[10, 20, 25]}
          disableRowSelectionOnClick
          sx={{
            "& .MuiDataGrid-cell": {
              whiteSpace: "normal",
              wordBreak: "break-word",
              lineHeight: "1.4",
              display: "flex",
              alignItems: "center",
            },
            "& .MuiDataGrid-columnHeaders": {
              whiteSpace: "normal",
            },
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
            "& .MuiDataGrid-cell:focus-within": {
              outline: "none",
            },
            "& .Mui-selected:hover": {
              backgroundColor: "transparent !important",
            },
          }}
        />
      </div>

      <ProjectCodeCreateDialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onCreated={() => {
          setOpenCreate(false);
          triggerRefresh();
        }}
      />

      <ProjectCodeEditDialog
        open={openEdit}
        onClose={() => {
          setOpenEdit(false);
          setSelectedRow(null);
        }}
        record={selectedRow}
        onUpdated={() => {
          setOpenEdit(false);
          setSelectedRow(null);
          triggerRefresh();
        }}
      />

      <ProjectCodeDeleteDialog
        open={openDelete}
        record={selectedRow}
        onClose={() => {
          setOpenDelete(false);
          setSelectedRow(null);
        }}
        onDeleted={() => {
          setOpenDelete(false);
          setSelectedRow(null);
          triggerRefresh();
        }}
      />

      <SubProjectCodeCreate
        open={openCreateSub}
        onClose={() => {
          setOpenCreateSub(false);
          setSelectedProjectForSub(null);
        }}
        onCreated={() => {
          setOpenCreateSub(false);
          setSelectedProjectForSub(null);
          triggerRefresh();
        }}
        defaultProjectCode={selectedProjectForSub?.__raw}
      />
    </div>
  );
}

export default ProjectCode;